import React, { ReactNode } from "react";
import { Stack, Typography } from "@mui/material";
import IconButton from "../ui/IconButton";
import LoadingButton from "../ui/LoadingButton";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { useIntl } from "../../contexts/IntlContext";
import { fontWeights } from "../../themes/fontWeights";

export type BreadcrumbItem = {
  label: ReactNode;
  onClick?: () => void;
};

type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  onBack?: () => void;
};

export function Breadcrumbs({ items, onBack }: BreadcrumbsProps) {
  const intl = useIntl();

  return (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
      {onBack ? (
        <IconButton
          size="small"
          onClick={onBack}
          sx={{ mr: 1 }}
          aria-label={intl.formatMessage({ id: "common.back" })}
        >
          <ArrowBackIosNewRoundedIcon fontSize="inherit" />
        </IconButton>
      ) : null}
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <React.Fragment key={index}>
            {index > 0 ? <Typography color="text.secondary">/</Typography> : null}
            {item.onClick && !isLast ? (
              <LoadingButton
                variant="text"
                onClick={item.onClick}
                sx={{
                  textTransform: "none",
                  color: "text.secondary",
                  textDecoration: "underline",
                  "&:hover": { textDecoration: "underline" },
                  minWidth: 0,
                  p: 0,
                  fontWeight: (theme) => theme.typography.fontWeightRegular,
                  fontSize: (theme) => theme.typography.body1.fontSize,
                  lineHeight: (theme) => theme.typography.body1.lineHeight,
                }}
              >
                {item.label}
              </LoadingButton>
            ) : (
              <Typography
                variant="body1"
                fontWeight={isLast ? fontWeights.semiBold : 400}
                color={isLast ? "text.primary" : "text.secondary"}
                aria-current={isLast ? "page" : undefined}
              >
                {item.label}
              </Typography>
            )}
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

export default Breadcrumbs;
